/**
 * Image Processor for Card Scanner
 *
 * Handles image preprocessing to improve OCR accuracy:
 * - Contrast enhancement
 * - Noise reduction
 * - Card region detection
 * - Perspective correction
 */

export interface ProcessedImage {
  original: ImageData;
  processed: ImageData;
  canvas: HTMLCanvasElement;
  cardRegion?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

/**
 * Capture a frame from video element
 */
export function captureFrame(video: HTMLVideoElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(video, 0, 0);
  return canvas;
}

/**
 * Enhance contrast using histogram equalization
 */
function enhanceContrast(imageData: ImageData): ImageData {
  const data = imageData.data;
  const histogram = new Array(256).fill(0);

  // Calculate histogram (grayscale)
  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    histogram[gray]++;
  }

  // Calculate cumulative distribution function
  const cdf = new Array(256);
  cdf[0] = histogram[0];
  for (let i = 1; i < 256; i++) {
    cdf[i] = cdf[i - 1] + histogram[i];
  }

  // Find min non-zero cdf value
  let cdfMin = 0;
  for (let i = 0; i < 256; i++) {
    if (cdf[i] > 0) {
      cdfMin = cdf[i];
      break;
    }
  }

  // Equalize
  const totalPixels = imageData.width * imageData.height;
  const output = new Uint8ClampedArray(data.length);

  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    const equalized = Math.round(((cdf[gray] - cdfMin) / (totalPixels - cdfMin)) * 255);

    // Apply equalization while maintaining color ratios
    const ratio = equalized / (gray || 1);
    output[i] = Math.min(255, Math.round(data[i] * ratio));
    output[i + 1] = Math.min(255, Math.round(data[i + 1] * ratio));
    output[i + 2] = Math.min(255, Math.round(data[i + 2] * ratio));
    output[i + 3] = data[i + 3];
  }

  return new ImageData(output, imageData.width, imageData.height);
}

/**
 * Apply sharpening filter
 */
function sharpen(imageData: ImageData): ImageData {
  const kernel = [
    0, -1, 0,
    -1, 5, -1,
    0, -1, 0
  ];

  return applyKernel(imageData, kernel, 3);
}

/**
 * Apply convolution kernel to image
 */
function applyKernel(imageData: ImageData, kernel: number[], size: number): ImageData {
  const { width, height, data } = imageData;
  const output = new Uint8ClampedArray(data.length);
  const half = Math.floor(size / 2);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let r = 0, g = 0, b = 0;

      for (let ky = 0; ky < size; ky++) {
        for (let kx = 0; kx < size; kx++) {
          const px = Math.min(width - 1, Math.max(0, x + kx - half));
          const py = Math.min(height - 1, Math.max(0, y + ky - half));
          const i = (py * width + px) * 4;
          const k = kernel[ky * size + kx];

          r += data[i] * k;
          g += data[i + 1] * k;
          b += data[i + 2] * k;
        }
      }

      const i = (y * width + x) * 4;
      output[i] = Math.min(255, Math.max(0, r));
      output[i + 1] = Math.min(255, Math.max(0, g));
      output[i + 2] = Math.min(255, Math.max(0, b));
      output[i + 3] = data[i + 3];
    }
  }

  return new ImageData(output, width, height);
}

/**
 * Convert to grayscale for OCR
 */
export function toGrayscale(imageData: ImageData): ImageData {
  const data = imageData.data;
  const output = new Uint8ClampedArray(data.length);

  for (let i = 0; i < data.length; i += 4) {
    const gray = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    output[i] = gray;
    output[i + 1] = gray;
    output[i + 2] = gray;
    output[i + 3] = data[i + 3];
  }

  return new ImageData(output, imageData.width, imageData.height);
}

/**
 * Apply threshold for binary image (better for OCR)
 */
export function threshold(imageData: ImageData, value: number = 128): ImageData {
  const data = imageData.data;
  const output = new Uint8ClampedArray(data.length);

  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    const binary = gray > value ? 255 : 0;
    output[i] = binary;
    output[i + 1] = binary;
    output[i + 2] = binary;
    output[i + 3] = data[i + 3];
  }

  return new ImageData(output, imageData.width, imageData.height);
}

/**
 * Crop image to specific region
 */
export function cropRegion(
  canvas: HTMLCanvasElement,
  region: { x: number; y: number; width: number; height: number }
): HTMLCanvasElement {
  const cropped = document.createElement('canvas');
  cropped.width = region.width;
  cropped.height = region.height;

  const ctx = cropped.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(
    canvas,
    region.x, region.y, region.width, region.height,
    0, 0, region.width, region.height
  );

  return cropped;
}

/**
 * Extract card name region (top of card)
 */
export function extractNameRegion(canvas: HTMLCanvasElement): HTMLCanvasElement {
  // Pokemon cards have the name in the top ~15% of the card
  const nameRegion = {
    x: Math.floor(canvas.width * 0.1),
    y: Math.floor(canvas.height * 0.02),
    width: Math.floor(canvas.width * 0.7),
    height: Math.floor(canvas.height * 0.08)
  };

  return cropRegion(canvas, nameRegion);
}

/**
 * Extract set number region (bottom of card)
 */
export function extractSetNumberRegion(canvas: HTMLCanvasElement): HTMLCanvasElement {
  // Set number is typically in the bottom left
  const setRegion = {
    x: Math.floor(canvas.width * 0.03),
    y: Math.floor(canvas.height * 0.88),
    width: Math.floor(canvas.width * 0.3),
    height: Math.floor(canvas.height * 0.08)
  };

  return cropRegion(canvas, setRegion);
}

/**
 * Process image for OCR
 */
export async function processForOCR(canvas: HTMLCanvasElement): Promise<ProcessedImage> {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  const original = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // Apply preprocessing pipeline
  let processed = enhanceContrast(original);
  processed = sharpen(processed);

  // Create output canvas
  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = canvas.width;
  outputCanvas.height = canvas.height;
  const outputCtx = outputCanvas.getContext('2d');
  if (!outputCtx) throw new Error('Could not get output canvas context');

  outputCtx.putImageData(processed, 0, 0);

  return {
    original,
    processed,
    canvas: outputCanvas
  };
}

/**
 * Resize image for AI processing (reduce data size)
 */
export function resizeForAI(
  canvas: HTMLCanvasElement,
  maxDimension: number = 1024
): HTMLCanvasElement {
  const { width, height } = canvas;
  const scale = Math.min(1, maxDimension / Math.max(width, height));

  const resized = document.createElement('canvas');
  resized.width = Math.floor(width * scale);
  resized.height = Math.floor(height * scale);

  const ctx = resized.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(canvas, 0, 0, resized.width, resized.height);
  return resized;
}

/**
 * Convert canvas to base64 data URL
 */
export function canvasToDataURL(canvas: HTMLCanvasElement, quality: number = 0.8): string {
  return canvas.toDataURL('image/jpeg', quality);
}

/**
 * Convert canvas to blob for upload
 */
export function canvasToBlob(canvas: HTMLCanvasElement, quality: number = 0.8): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create blob'));
      },
      'image/jpeg',
      quality
    );
  });
}
